import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { CalendarBlank, Plus, PencilSimple, Trash, Wine, Users, Phone, MapPin } from "@phosphor-icons/react";
import { useRestaurant } from "../contexts/RestaurantContext";
import { format } from "date-fns";

const EVENT_TYPES = [
  { value: "private_dinner", label: "Private Dinner" },
  { value: "banquet", label: "Banquet" },
  { value: "corporate", label: "Corporate Event" },
  { value: "wedding", label: "Wedding Reception" },
  { value: "birthday", label: "Birthday Party" },
  { value: "holiday", label: "Holiday Event" },
  { value: "custom", label: "Custom" },
];

const STATUS_STYLES = {
  planned: { bg: "#F4A26120", color: "#F4A261", label: "Planned" },
  confirmed: { bg: "#2F4F4F15", color: "#2F4F4F", label: "Confirmed" },
  in_progress: { bg: "#81B29A20", color: "#81B29A", label: "In Progress" },
  completed: { bg: "#57534E15", color: "#57534E", label: "Completed" },
  cancelled: { bg: "#9CA3AF15", color: "#9CA3AF", label: "Cancelled" },
};

export default function EventsPage() {
  const { restId } = useRestaurant();
  const [events, setEvents] = useState([]);
  const [floors, setFloors] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [form, setForm] = useState({
    name: "", event_type: "private_dinner", date: format(new Date(), "yyyy-MM-dd"),
    time: "18:00", end_time: "22:00", guest_count: 10, contact_name: "",
    contact_phone: "", floor_id: "", notes: "", status: "planned",
  });

  const fetchEvents = useCallback(async () => {
    try {
      let params = restId ? `?restaurant_id=${restId}` : "?";
      if (selectedDate) params += `&date=${format(selectedDate, "yyyy-MM-dd")}`;
      const { data } = await api.get(`/events${params}`);
      setEvents(data);
    } catch { toast.error("Failed to load events"); }
  }, [restId, selectedDate]);

  const fetchFloors = useCallback(async () => {
    try {
      const params = restId ? `?restaurant_id=${restId}` : "";
      const { data } = await api.get(`/floors${params}`);
      setFloors(data);
    } catch { /* silent */ }
  }, [restId]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);
  useEffect(() => { fetchFloors(); }, [fetchFloors]);

  const createEvent = async () => {
    if (!form.name.trim()) return;
    try {
      await api.post("/events", { ...form, restaurant_id: restId, floor_id: form.floor_id || null });
      setShowAdd(false);
      resetForm();
      fetchEvents();
      toast.success("Event created");
    } catch { toast.error("Failed"); }
  };

  const updateEvent = async () => {
    if (!editingEvent) return;
    try {
      await api.put(`/events/${editingEvent.id}`, form);
      setShowEdit(false);
      setEditingEvent(null);
      fetchEvents();
      toast.success("Event updated");
    } catch { toast.error("Failed"); }
  };

  const deleteEvent = async (id) => {
    try {
      await api.delete(`/events/${id}`);
      fetchEvents();
      toast.success("Event deleted");
    } catch { toast.error("Failed"); }
  };

  const changeStatus = async (id, status) => {
    try {
      await api.put(`/events/${id}`, { status });
      fetchEvents();
    } catch { toast.error("Failed"); }
  };

  const openEdit = (ev) => {
    setEditingEvent(ev);
    setForm({
      name: ev.name, event_type: ev.event_type, date: ev.date, time: ev.time,
      end_time: ev.end_time || "", guest_count: ev.guest_count, contact_name: ev.contact_name || "",
      contact_phone: ev.contact_phone || "", floor_id: ev.floor_id || "", notes: ev.notes || "", status: ev.status,
    });
    setShowEdit(true);
  };

  const resetForm = () => setForm({
    name: "", event_type: "private_dinner", date: format(new Date(), "yyyy-MM-dd"),
    time: "18:00", end_time: "22:00", guest_count: 10, contact_name: "",
    contact_phone: "", floor_id: "", notes: "", status: "planned",
  });

  const EventForm = ({ onSubmit, buttonLabel }) => (
    <div className="space-y-4 mt-2 max-h-[60vh] overflow-y-auto pr-1">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Event Name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Smith Wedding" className="mt-1.5" data-testid="event-name-input" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Type</Label>
          <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
            <SelectTrigger className="mt-1.5" data-testid="event-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {EVENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Date</Label>
          <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" data-testid="event-date-input" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Start</Label>
          <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="mt-1.5" data-testid="event-time-input" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">End</Label>
          <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="mt-1.5" data-testid="event-end-time-input" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Guest Count</Label>
          <Input type="number" min={1} value={form.guest_count} onChange={(e) => setForm({ ...form, guest_count: parseInt(e.target.value) || 1 })} className="mt-1.5" data-testid="event-guests-input" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Floor</Label>
          <Select value={form.floor_id || "none"} onValueChange={(v) => setForm({ ...form, floor_id: v === "none" ? "" : v })}>
            <SelectTrigger className="mt-1.5" data-testid="event-floor-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No floor</SelectItem>
              {floors.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Contact Name</Label>
          <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-1.5" data-testid="event-contact-input" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Contact Phone</Label>
          <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="mt-1.5" data-testid="event-phone-input" />
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Notes</Label>
        <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Special requirements, menu notes..." className="mt-1.5" data-testid="event-notes-input" />
      </div>
      <Button onClick={onSubmit} style={{ backgroundColor: "#2F4F4F" }} className="w-full" data-testid="event-submit-btn">{buttonLabel}</Button>
    </div>
  );

  const upcoming = events.filter((e) => e.status !== "completed" && e.status !== "cancelled");
  const past = events.filter((e) => e.status === "completed" || e.status === "cancelled");

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="events-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>Events</h2>
          <p className="text-xs sm:text-sm text-stone-500 mt-1">{upcoming.length} upcoming event{upcoming.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="text-xs sm:text-sm" data-testid="event-date-filter">
                <CalendarBlank size={16} className="mr-1" /> {selectedDate ? format(selectedDate, "MMM d") : "All Dates"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar mode="single" selected={selectedDate} onSelect={(d) => setSelectedDate(d || null)} />
              {selectedDate && <button onClick={() => setSelectedDate(null)} className="w-full text-xs text-stone-500 py-2 hover:text-stone-600">Clear filter</button>}
            </PopoverContent>
          </Popover>
          <Button onClick={() => { resetForm(); setShowAdd(true); }} style={{ backgroundColor: "#2F4F4F" }} size="sm" data-testid="add-event-btn" className="text-xs sm:text-sm">
            <Plus size={16} className="mr-1" /> New Event
          </Button>
        </div>
      </div>

      {events.length === 0 ? (
        <div className="text-center py-16 animate-fade-in">
          <Wine size={48} className="mx-auto mb-3 text-stone-300" weight="duotone" />
          <p className="text-stone-500 text-sm">No events {selectedDate ? "for this date" : "yet"}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {upcoming.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-3">Upcoming</h3>
              <div className="space-y-3">
                {upcoming.map((ev, idx) => {
                  const st = STATUS_STYLES[ev.status] || STATUS_STYLES.planned;
                  const type = EVENT_TYPES.find((t) => t.value === ev.event_type);
                  return (
                    <div key={ev.id} className="bg-white rounded-lg border border-stone-200 p-4 animate-fade-in" style={{ animationDelay: `${idx * 50}ms` }} data-testid={`event-card-${ev.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-stone-800" style={{ fontFamily: "Work Sans" }}>{ev.name}</span>
                            <Badge style={{ backgroundColor: st.bg, color: st.color, border: "none" }}>{st.label}</Badge>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-500">{type?.label || ev.event_type}</span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-stone-500 flex-wrap">
                            <span className="flex items-center gap-1"><CalendarBlank size={12} /> {ev.date} &middot; {ev.time}{ev.end_time ? ` - ${ev.end_time}` : ""}</span>
                            <span className="flex items-center gap-1"><Users size={12} /> {ev.guest_count} guests</span>
                            {ev.contact_name && <span className="flex items-center gap-1"><Phone size={12} /> {ev.contact_name} {ev.contact_phone}</span>}
                            {ev.floor_id && <span className="flex items-center gap-1"><MapPin size={12} /> {floors.find((f) => f.id === ev.floor_id)?.name || "Floor"}</span>}
                          </div>
                          {ev.notes && <p className="text-xs text-stone-500 mt-1.5">{ev.notes}</p>}
                        </div>
                        <div className="flex items-center gap-1 ml-3">
                          {ev.status === "planned" && (
                            <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => changeStatus(ev.id, "confirmed")} data-testid={`confirm-event-${ev.id}`}>Confirm</Button>
                          )}
                          {ev.status === "confirmed" && (
                            <Button variant="outline" size="sm" className="text-xs h-7 text-green-700" onClick={() => changeStatus(ev.id, "in_progress")} data-testid={`start-event-${ev.id}`}>Start</Button>
                          )}
                          {ev.status === "in_progress" && (
                            <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => changeStatus(ev.id, "completed")} data-testid={`complete-event-${ev.id}`}>Complete</Button>
                          )}
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(ev)} data-testid={`edit-event-${ev.id}`}><PencilSimple size={14} /></Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => deleteEvent(ev.id)} data-testid={`delete-event-${ev.id}`}><Trash size={14} /></Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {past.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-3">Past Events</h3>
              <div className="space-y-2">
                {past.map((ev) => {
                  const st = STATUS_STYLES[ev.status] || STATUS_STYLES.completed;
                  return (
                    <div key={ev.id} className="bg-stone-50 rounded-lg border border-stone-100 p-3 flex items-center justify-between opacity-70" data-testid={`past-event-${ev.id}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-stone-600">{ev.name}</span>
                        <Badge style={{ backgroundColor: st.bg, color: st.color, border: "none" }} className="text-[10px]">{st.label}</Badge>
                        <span className="text-xs text-stone-500">{ev.date}</span>
                        <span className="text-xs text-stone-500">{ev.guest_count} guests</span>
                      </div>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-300" onClick={() => deleteEvent(ev.id)}><Trash size={14} /></Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-event-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>New Event</DialogTitle></DialogHeader>
          <EventForm onSubmit={createEvent} buttonLabel="Create Event" />
        </DialogContent>
      </Dialog>

      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent data-testid="edit-event-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>Edit Event</DialogTitle></DialogHeader>
          <EventForm onSubmit={updateEvent} buttonLabel="Save Changes" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
